import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { ONBOARDING_STEPS } from '../../shared/OnboardingOptions';
import { SUBSCRIPTION_PLANS } from '../../shared/Capabilities';
import StartStage from './StartStage';
import { parseQuery } from '../../common/utils';
import CancelStage from './Stages/CancelStage';
import Profile from '../Profile/Profile';
import { PROFILE_LINK, SUBSCRIPTIONS_LINK } from '../../common/urlLinks';
import OnboardingShell, { useOnboardingStyles } from './OnboardingShell';
import GuestSignInOptions from './GuestSignInOptions';
import { GA_EVENT_TREE } from '../../gaConstants';
import Subscriptions from '../Subscriptions/Subscriptions';

function buildState(queryData) {
    let state = {
        billAnnual: false,
        billEur: true,
        step: ONBOARDING_STEPS.START,
        subscription: SUBSCRIPTION_PLANS.FREE,
    };

    if (!queryData) return state;

    if (queryData.step && ONBOARDING_STEPS[queryData.step]) {
        state.step = queryData.step;
    }
    if (queryData.subscription && SUBSCRIPTION_PLANS[queryData.subscription]) {
        state.subscription = queryData.subscription;
    }
    if (queryData.billAnnual && queryData.billAnnual.toLowerCase() === 'true') {
        queryData.billAnnual = true;
    } else {
        queryData.billAnnual = false;
    }
    if (queryData.billEur && queryData.billEur.toLowerCase() === 'false') {
        queryData.billEur = false;
    } else {
        queryData.billEur = true;
    }

    return queryData;
}

// A guest who picked a trial or a paid plan: guests cannot subscribe, so ask
// them to sign in with a full account and send them back to this very plan
// once the login completes.
const GuestStage = ({ state, intl, returnTo }) => {
    const classes = useOnboardingStyles();
    const plan = intl.formatMessage({ id: `subscriptions.subscription.${state.subscription}.name` });

    return (
        <OnboardingShell
            title={<FormattedMessage id="onboarding.guest.title" />}
            lead={<FormattedMessage id="onboarding.guest.lead" values={{ plan }} />}
            maxWidth={760}
        >
            <div className={classes.card}>
                <GuestSignInOptions
                    returnTo={returnTo}
                    backTo={SUBSCRIPTIONS_LINK}
                    backLabelId="profile.guest.switch.back"
                />
            </div>
        </OnboardingShell>
    );
};

const Onboarding = ({ profile, language, handleOpen, open, handleLogin, intl }) => {
    const location = useLocation();
    const history = useHistory();

    let queryObj = parseQuery(location.search);

    const state = buildState(queryObj);

    // Picking any of these needs an account. Landing here logged out used to
    // dead-end: FREE rendered nothing at all, and BASIC/PREMIUM fell through to
    // the "validate your email" screen, which makes no sense before signing in.
    const needsAccount =
        state.step === ONBOARDING_STEPS.START &&
        [
            SUBSCRIPTION_PLANS.FREE,
            SUBSCRIPTION_PLANS.TRIAL,
            SUBSCRIPTION_PLANS.BASIC,
            SUBSCRIPTION_PLANS.PREMIUM,
        ].includes(state.subscription);
    const mustLogIn = !profile.loggedIn && needsAccount;

    React.useEffect(() => {
        if (!mustLogIn) return;
        // index.js replaces the location with pathBeforeLogin once the OAuth
        // round trip finishes, so the visitor comes back to the plan they chose
        // and the stage below renders for real.
        window.localStorage.setItem('pathBeforeLogin', location.pathname + location.search);
        // The same card the Login button in the header opens.
        handleOpen(true);
        // location.key: the page behind the card has its own "start for free"
        // links to this very URL. Pushing it again changes neither path nor
        // query, only the key, and the card has to come back for that click.
    }, [mustLogIn, location.pathname, location.search, location.key, handleOpen]);

    // A signed-in, non-guest account landing on the free plan: logged once per
    // arrival (the click or the login round trip), not on every render.
    const startsFree = profile.loggedIn && !profile.isGuest && state.subscription === SUBSCRIPTION_PLANS.FREE;
    React.useEffect(() => {
        if (startsFree) {
            window.logAppActivity(GA_EVENT_TREE.mainPage.onboarding.startFree);
        }
    }, [startsFree]);

    if (!profile.loggedIn) {
        // The plan list sits behind the modal, so dismissing it leaves the
        // visitor somewhere useful rather than on a blank page.
        if (state.step === ONBOARDING_STEPS.START && !needsAccount) {
            return <StartStage state={state} profile={profile} language={language} />;
        }
        return <Subscriptions language={language} handleOpen={handleOpen} open={open} handleLogin={handleLogin} />;
    }

    if (profile.isGuest) {
        // guests cannot start trials or paid subscriptions
        if (needsAccount && state.subscription !== SUBSCRIPTION_PLANS.FREE) {
            return <GuestStage state={state} intl={intl} returnTo={location.pathname + location.search} />;
        }
        history.push(PROFILE_LINK);
        return (
            <React.Fragment>
                <Profile />
            </React.Fragment>
        );
    }

    if (state.subscription === SUBSCRIPTION_PLANS.FREE) {
        history.push(PROFILE_LINK);
    }

    if (state.step === ONBOARDING_STEPS.START) {
        return <StartStage state={state} profile={profile} language={language} />;
    }

    if (state.step === ONBOARDING_STEPS.CANCEL) {
        return <CancelStage />;
    }

    return (
        <React.Fragment>
            <Profile />
        </React.Fragment>
    );
};

const mapStateToProps = (state) => {
    const { profile, mainAppState } = state;
    return { profile, subscriptions: mainAppState.subscriptions, pricing: mainAppState.pricing };
};

export default injectIntl(connect(mapStateToProps)(Onboarding));
